import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const WhatsAppButton = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  
  const whatsappNumber = "923185194055";
  const message = encodeURIComponent("Hello! I'm interested in your software development services.");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

  // Hide tooltip after 5 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => setShowTooltip(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50">
      {/* Tooltip - hidden on small screens */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.8 }}
            className="absolute bottom-14 md:bottom-16 right-0 bg-white text-slate-800 rounded-lg px-3 py-1.5 md:px-4 md:py-2 shadow-lg whitespace-nowrap flex items-center gap-2 hidden sm:flex"
          >
            <span className="text-sm font-medium">Chat with us!</span>
            <button 
              onClick={(e) => {
                e.preventDefault();
                setShowTooltip(false);
              }}
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
            {/* Tooltip arrow */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white transform rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Button */}
      <motion.a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className="relative w-12 h-12 md:w-14 md:h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg cursor-pointer group"
        style={{
          boxShadow: isHovered 
            ? '0 8px 40px rgba(37, 211, 102, 0.6)' 
            : '0 4px 20px rgba(37, 211, 102, 0.4)',
        }}
        data-testid="whatsapp-btn"
        aria-label="Chat on WhatsApp"
      >
        {/* Icon with animation */}
        <motion.div
          animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
          transition={{ duration: 0.5 }}
        >
          <MessageCircle size={24} className="text-white md:w-7 md:h-7" />
        </motion.div>
        
        {/* Pulse rings */}
        <motion.span 
          className="absolute inset-0 rounded-full bg-[#25D366]"
          animate={{ 
            scale: [1, 1.5, 1.8],
            opacity: [0.4, 0.2, 0],
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "easeOut",
          }}
        />
        <motion.span 
          className="absolute inset-0 rounded-full bg-[#25D366]"
          animate={{ 
            scale: [1, 1.3, 1.6],
            opacity: [0.3, 0.15, 0],
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "easeOut",
            delay: 0.5,
          }}
        />

        {/* Send icon on hover */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Send size={12} className="text-[#25D366]" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Notification dot */}
        <motion.div
          className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-[8px] text-white font-bold">1</span>
        </motion.div>
      </motion.a>
    </div>
  );
};

export default WhatsAppButton;
